/**
 * B1076 — Réglages du « Rapport forfait AI » (Eric 2026-09-05 SPEC STRICK PROD).
 *
 * Le rapport de consommation existait, mais n'était envoyé à personne : il
 * fallait penser à ouvrir la page. Chaque organisation choisit ici ses
 * destinataires et sa périodicité — et la fenêtre de calcul suit la
 * périodicité, sinon le montant affiché ne correspondrait pas à la période
 * annoncée.
 *
 * Même présentation que « Réglages de la surveillance » (page Sentinelle) :
 * interrupteur, cases à cocher, menu de périodicité.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import { Gear, PaperPlaneTilt, TrendUp, TrendDown, Warning } from "@phosphor-icons/react";
import ReportSendLog from "./ReportSendLog";
import ReportPreviewModal from "./ReportPreviewModal";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export const AIReportSettingsCard = () => {
  const { tr, lang } = useLang();
  const [settings, setSettings] = useState(null);
  const [alert, setAlert] = useState(null);
  const [budgetAlert, setBudgetAlert] = useState(null);
  const [logKey, setLogKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState("");
  const [customEmail, setCustomEmail] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/org/ai-package/report-settings`, {
        withCredentials: true,
      });
      setSettings(data);
    } catch {
      setSettings(null);
    }
    // B1077 — L'écart de consommation est chargé séparément : le calcul de
    // deux périodes ne doit pas retarder l'affichage des réglages.
    try {
      const { data } = await axios.get(`${API}/api/org/ai-package/consumption-alert`, {
        withCredentials: true,
      });
      setAlert(data);
    } catch {
      setAlert(null);
    }
    // B1084 — Dépassement anticipé : bandeau visible sans attendre le rapport.
    try {
      const { data } = await axios.get(`${API}/api/org/ai-package/budget-alert`, {
        withCredentials: true,
      });
      setBudgetAlert(data);
    } catch {
      setBudgetAlert(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // B1111 (Eric 2026-09-08) — Suppression d'un destinataire ajouté à la main.
  const removeRecipient = async (email) => {
    const ok = await confirmDialog(
      tr(
        `Retirer définitivement ${email} de la liste des destinataires ?`,
        `Permanently remove ${email} from the recipient list?`,
      )
    );
    if (!ok) return;
    setSaving(true);
    try {
      const { data } = await axios.delete(
        `${API}/api/org/ai-package/report-settings/recipients/${encodeURIComponent(email)}`,
        { withCredentials: true }
      );
      setSettings(data);
      toast.success(tr(`${email} retiré de la liste`, `${email} removed from the list`));
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Suppression impossible", "Could not remove"));
    } finally {
      setSaving(false);
    }
  };

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/api/org/ai-package/report-settings`,
        patch,
        { withCredentials: true }
      );
      setSettings(data);
      toast.success(tr("Réglages du rapport enregistrés", "Report settings saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Enregistrement impossible", "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  const toggleRecipient = (email) => {
    const current = (settings?.candidates || []).filter((c) => c.selected).map((c) => c.email);
    const next = current.includes(email)
      ? current.filter((e) => e !== email)
      : [...current, email];
    save({ recipients: next });
  };

  // B1082 — Courriel de vérification à UNE adresse : activer une liste jamais
  // testée, c'est découvrir un mois plus tard que rien n'est arrivé.
  const verify = async (email) => {
    if (!email) return;
    setVerifying(email);
    try {
      const { data } = await axios.post(
        `${API}/api/org/ai-package/report-settings/verify`,
        { email },
        { withCredentials: true }
      );
      if (data.sent) {
        toast.success(tr(`Courriel de vérification envoyé à ${data.email}`, `Verification email sent to ${data.email}`));
      } else {
        toast.info(
          tr(
            `${data.email} : aucun courriel envoyé (${data.reason}). ` +
              (data.verified ? "Adresse marquée vérifiée." : ""),
            `${data.email}: no email sent (${data.reason}). ` +
              (data.verified ? "Address marked as verified." : ""),
          )
        );
      }
      setCustomEmail("");
      setLogKey((k) => k + 1);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Vérification impossible", "Verification failed"));
    } finally {
      setVerifying("");
    }
  };

  const sendNow = async () => {    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/api/org/ai-package/report-settings/send-now`,
        {},
        { withCredentials: true }
      );
      if (data.sent) {
        toast.success(tr(`Rapport envoyé à ${data.recipients.join(", ")}`, `Report sent to ${data.recipients.join(", ")}`));
      } else {
        toast.info(`${tr("Rapport non envoyé : ", "Report not sent: ")}${data.reason || tr("envoi désactivé sur cet environnement", "sending disabled on this environment")}`);
      }
      setLogKey((k) => k + 1);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Envoi impossible", "Could not send"));
    } finally {
      setSending(false);
    }
  };

  if (!settings) return null;
  const selectedCount = (settings.candidates || []).filter((c) => c.selected).length;
  const verifiedSelectedCount = (settings.candidates || []).filter(
    (c) => c.selected && c.verified_at
  ).length;

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white p-5"
      data-testid="ai-report-settings"
    >
      <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
        <Gear size={18} />
        {tr("Rapport forfait AI — destinataires et périodicité", "AI package report — recipients and frequency")}
      </h2>
      <p className="text-xs text-zinc-500 mt-0.5 mb-4 max-w-3xl">
        {tr("Réglages propres à ", "Settings specific to ")}<strong>{tr("cette organisation", "this organisation")}</strong>
        {tr(
          ". Les chiffres du rapport sont calculés sur la période choisie : changer la périodicité change aussi la fenêtre de calcul, pour que le montant corresponde vraiment à ce qui a été consommé.",
          ". The report figures are computed over the chosen period: changing the frequency also changes the calculation window, so the amount really matches what was used.",
        )}
      </p>

      {/* B1084 — Dépassement anticipé : prévenir dès 90 % du budget projeté,
          sans attendre le prochain rapport. */}
      {budgetAlert?.status === "breach" && (
        <div
          className="mb-5 flex items-start gap-3 rounded-lg border-l-4 border-red-600 bg-red-50 px-4 py-3"
          data-testid="ai-report-budget-alert"
        >
          <Warning size={20} className="mt-0.5 text-red-600 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-red-800">
              {tr("Dépassement de budget prévu en", "Budget overrun expected in")} {budgetAlert.month_label}
            </div>
            <p
              className="text-xs text-zinc-700 mt-0.5 max-w-2xl"
              data-testid="ai-report-budget-alert-message"
            >
              {budgetAlert.message}
            </p>
            <p className="text-[11px] text-zinc-500 mt-1">
              Seuil d'alerte : {budgetAlert.warn_ratio_pct} % du budget · un incident
              {tr("est ouvert au journal et les destinataires vérifiés ont reçu un courriel ·", "is opened in the journal and the verified recipients got an email ·")}
              rappel uniquement si la situation s'aggrave de plus de{" "}
              {budgetAlert.escalation_pct} %.
            </p>
          </div>
        </div>
      )}

      {/* B1077 — Bandeau d'écart : rouge pour une hausse, bleu pour une baisse
          (une chute de moitié vient souvent d'un pipeline arrêté). Rien du tout
          quand c'est stable, pour ne pas habituer l'œil à ignorer ce bloc. */}
      {alert && (alert.status === "spike" || alert.status === "drop") && (
        <div
          className={`mb-5 flex items-start gap-3 rounded-lg border-l-4 px-4 py-3 ${
            alert.status === "spike"
              ? "border-red-600 bg-red-50"
              : "border-blue-600 bg-blue-50"
          }`}
          data-testid="ai-report-consumption-alert"
        >
          {alert.status === "spike" ? (
            <TrendUp size={20} className="mt-0.5 text-red-600 shrink-0" />
          ) : (
            <TrendDown size={20} className="mt-0.5 text-blue-600 shrink-0" />
          )}
          <div>
            <div
              className={`text-sm font-semibold ${
                alert.status === "spike" ? "text-red-800" : "text-blue-800"
              }`}
              data-testid="ai-report-consumption-alert-title"
            >
              {alert.status === "spike"
                ? tr("Hausse anormale de consommation", "Unusual spending spike")
                : "Baisse anormale de consommation"}
            </div>
            <p className="text-xs text-zinc-700 mt-0.5 max-w-2xl" data-testid="ai-report-consumption-alert-message">
              {alert.message}
            </p>
            <p className="text-[11px] text-zinc-500 mt-1">
              {tr("Seuil", "Threshold")} : ±{alert.spike_threshold_pct} % · {tr("comparaison ignorée sous", "comparison skipped below")}{" "}
              {alert.min_baseline_cad} $ de consommation précédente · un incident
              {tr("est ouvert au journal, comme pour la Sentinelle.", "is opened in the journal, just like the Sentinel.")}
            </p>

            {/* B1079 — Variations par poste : « la facture monte » ne dit pas
                quoi corriger, « la transcription a triplé » si. */}
            <div className="mt-3 overflow-x-auto rounded-lg border border-zinc-200 bg-white">
              <table className="w-full text-left text-[11px]" data-testid="ai-report-breakdown-table">
                <thead className="bg-zinc-50 text-zinc-500">
                  <tr>
                    <th className="py-1.5 px-2.5 font-semibold">Poste</th>
                    <th className="py-1.5 px-2.5 font-semibold text-right">{tr("Période précédente", "Previous period")}</th>
                    <th className="py-1.5 px-2.5 font-semibold text-right">{tr("Période courante", "Current period")}</th>
                    <th className="py-1.5 px-2.5 font-semibold text-right">Variation</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { heading: "Par usage", rows: alert.by_usage || [] },
                    { heading: "Par fournisseur", rows: alert.by_provider || [] },
                  ].map((group) => (
                    <Fragment key={group.heading}>
                      <tr className="bg-zinc-50/70">
                        <td colSpan={4} className="py-1 px-2.5 font-semibold text-zinc-500 uppercase tracking-wide">
                          {group.heading}
                        </td>
                      </tr>
                      {group.rows.map((r) => (
                        <tr
                          key={`${group.heading}-${r.key}`}
                          className="border-t border-zinc-100"
                          data-testid={`ai-report-breakdown-row-${r.group}-${r.key}`}
                        >
                          <td className="py-1.5 px-2.5 text-zinc-700">{r.label}</td>
                          <td className="py-1.5 px-2.5 text-right text-zinc-600">
                            {r.previous_cad.toFixed(2)} $
                          </td>
                          <td className="py-1.5 px-2.5 text-right text-zinc-800">
                            {r.current_cad.toFixed(2)} $
                          </td>
                          <td
                            className={`py-1.5 px-2.5 text-right font-medium ${
                              r.delta_cad > 0
                                ? "text-red-700"
                                : r.delta_cad < 0
                                ? "text-blue-700"
                                : "text-zinc-500"
                            }`}
                          >
                            {r.change_pct === null ? "—" : `${r.change_pct > 0 ? "+" : ""}${r.change_pct} %`}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-zinc-900">
            {tr("Envoi", "Sending")} {settings.enabled ? tr("activé", "on") : tr("en pause", "paused")}
          </div>
          <p className="text-xs text-zinc-500 max-w-xl">
            {tr(
              "En pause, aucun rapport ne part, quel que soit le nombre de destinataires cochés — pratique pendant la configuration d'une nouvelle organisation.",
              "While paused, no report goes out no matter how many recipients are ticked — handy while setting up a new organisation.",
            )}
          </p>
          {!verifiedSelectedCount && (
            <p
              className="text-xs text-amber-700 mt-1 max-w-xl"
              data-testid="ai-report-verify-required"
            >
              {tr(
                "Activation bloquée : envoyez d'abord un courriel de vérification à au moins un destinataire coché.",
                "Cannot enable yet: send a verification email to at least one ticked recipient first.",
              )}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => save({ enabled: !settings.enabled })}
          disabled={saving}
          className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
            settings.enabled ? "bg-[#002FA7]" : "bg-zinc-300"
          }`}
          role="switch"
          aria-checked={settings.enabled}
          data-testid="ai-report-toggle"
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
              settings.enabled ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
            {tr("Qui reçoit le rapport", "Who receives the report")} ({selectedCount})
          </div>
          {settings.recipients_are_default && (
            <p
              className="text-[11px] text-zinc-500 mb-2 max-w-md"
              data-testid="ai-report-recipients-default"
            >
              {tr(
                "Liste par défaut : les fondateurs de Knoria et les administrateurs techniques de cette organisation (B1101) — les dirigeants métier sont proposés mais décochés. Rien ne part avant que les envois soient activés et qu'une adresse ait été vérifiée.",
                "Default list: the Knoria founders and this organisation's technical admins (B1101) — business leaders are offered but unticked. Nothing goes out until sending is enabled and one address is verified.",
              )}
            </p>
          )}
          <div className="space-y-2" data-testid="ai-report-recipients">
            {(settings.candidates || []).map((c) => (
              <div
                key={c.email}
                className="flex items-start justify-between gap-2"
                data-testid={`ai-report-recipient-row-${c.email}`}
              >
                <label className="flex items-start gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={c.selected}
                    disabled={saving}
                    onChange={() => toggleRecipient(c.email)}
                    className="mt-1 h-4 w-4 rounded border-zinc-300 text-[#002FA7] focus:ring-[#002FA7]/30"
                    data-testid={`ai-report-recipient-checkbox-${c.email}`}
                  />
                  <span>
                    <span className="text-sm font-medium text-zinc-900 group-hover:text-[#002FA7]">
                      {c.name}
                    </span>
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#002FA7]/10 text-[#002FA7]">
                      {lang === "en" ? (c.role_en || c.role) : c.role}
                    </span>
                    <span className="block text-xs text-zinc-500">{c.email}</span>
                    {c.verified_at ? (
                      <span
                        className="block text-[11px] text-emerald-700"
                        data-testid={`ai-report-verified-${c.email}`}
                      >
                        {tr("Vérifiée le", "Verified on")}{" "}
                        {new Date(c.verified_at).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA", {
                          dateStyle: "long",
                          timeZone: "America/Toronto",
                        })}
                      </span>
                    ) : (
                      <span
                        className="block text-[11px] text-amber-700"
                        data-testid={`ai-report-unverified-${c.email}`}
                      >
                        {tr("Adresse jamais vérifiée", "Address never verified")}
                      </span>
                    )}
                  </span>
                </label>
                <div className="shrink-0 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => verify(c.email)}
                    disabled={!!verifying}
                    className="px-2 py-1 rounded-md border border-zinc-300 text-[11px] text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                    data-testid={`ai-report-verify-${c.email}`}
                  >
                    {verifying === c.email ? "Envoi…" : "Tester"}
                  </button>
                  {/* B1111 (Eric 2026-09-08) — Retirer une adresse ajoutée à
                      la main. Un fondateur ou un admin technique n'a pas ce
                      bouton : il se décoche, il ne s'efface pas. */}
                  {c.removable && (
                    <button
                      type="button"
                      onClick={() => removeRecipient(c.email)}
                      disabled={saving}
                      className="px-2 py-1 rounded-md border border-zinc-300 text-[11px] text-zinc-500 hover:bg-red-50 hover:text-red-700 hover:border-red-300 disabled:opacity-50"
                      data-testid={`ai-report-remove-${c.email}`}
                      title={tr(`Retirer définitivement ${c.email} de la liste`, `Permanently remove ${c.email} from the list`)}
                    >
                      {tr("Retirer", "Remove")}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!(settings.candidates || []).length && (
              <p className="text-xs text-zinc-500" data-testid="ai-report-no-candidates">
                {tr("Aucun administrateur ni fondateur dans cette organisation.", "No admin or founder in this organisation.")}
              </p>
            )}
          </div>

          {/* B1082 — Adresse libre : vérifier une boîte partagée (comptabilité,
              cabinet) avant de l'ajouter à la liste. */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              type="email"
              value={customEmail}
              onChange={(e) => setCustomEmail(e.target.value)}
              placeholder={tr("autre.adresse@cabinet.com", "other.address@firm.com")}
              className="flex-1 min-w-[200px] rounded-lg border border-zinc-300 px-3 py-2 text-sm"
              data-testid="ai-report-verify-email-input"
            />
            <button
              type="button"
              onClick={() => verify(customEmail.trim())}
              disabled={!!verifying || !customEmail.trim()}
              className="px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
              data-testid="ai-report-verify-email-btn"
            >
              {tr("Envoyer un courriel de vérification", "Send a verification email")}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              {tr("Périodicité d'envoi", "Sending frequency")}
            </span>
            <select
              value={settings.frequency}
              disabled={saving}
              onChange={(e) => save({ frequency: e.target.value })}
              className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm"
              data-testid="ai-report-frequency-select"
            >
              {(settings.frequencies || []).map((f) => (
                <option key={f.value} value={f.value}>
                  {lang === "en" ? (f.label_en || f.label) : f.label}
                </option>
              ))}
            </select>
            <span className="text-[11px] text-zinc-500">
              {tr("Fenêtre de calcul correspondante", "Matching calculation window")} :{" "}
              {(settings.frequencies || []).find((f) => f.value === settings.frequency)?.period ||
                "30d"}
              .
            </span>
          </label>

          {/* Heure d'envoi choisie par l'organisation : un rapport qui arrive
              pendant une rencontre client n'est jamais lu. */}
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              {tr("Heure d'envoi", "Sending time")}
            </span>
            <select
              value={settings.send_hour ?? 8}
              disabled={saving}
              onChange={(e) => save({ send_hour: Number(e.target.value) })}
              className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm"
              data-testid="ai-report-send-hour-select"
            >
              {(settings.allowed_send_hours || [8]).map((h) => (
                <option key={h} value={h}>
                  {lang === "fr" ? `${String(h).padStart(2, "0")} h 00` : `${String(h).padStart(2, "0")}:00`}
                </option>
              ))}
            </select>
            <span className="text-[11px] text-zinc-500">
              {tr(
                "Heure de Montréal. Le rapport part à partir de cette heure, le jour où la périodicité est atteinte.",
                "Montreal time. The report goes out from this time, on the day the frequency is reached.",
              )}
            </span>
          </label>

          {/* B1096 — Périodicité des RAPPELS d'alerte. Une hausse mensuelle
              met plusieurs jours à se résorber : un rappel toutes les heures
              n'apprend qu'une chose au lecteur, ignorer l'alerte. */}
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              {tr("Rappel des alertes", "Alerts reminder")}
            </span>
            <select
              value={settings.alert_reminder_days ?? 7}
              disabled={saving}
              onChange={(e) => save({ alert_reminder_days: Number(e.target.value) })}
              className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm"
              data-testid="ai-report-reminder-days-select"
            >
              {(settings.allowed_alert_reminder_days || [1, 3, 7, 14, 30]).map((d) => (
                <option key={d} value={d}>
                  {d === 1
                    ? tr("Chaque jour", "Every day")
                    : tr(`Tous les ${d} jours`, `Every ${d} days`)}
                </option>
              ))}
            </select>
            <span className="text-[11px] text-zinc-500">
              {tr(
                "Délai avant de rappeler une alerte encore active (hausse de consommation, dépassement de budget). Une aggravation de plus de 20 % passe outre le délai.",
                "Delay before reminding about an alert that is still active (consumption rise, budget overrun). A worsening of more than 20 % bypasses the delay.",
              )}
            </span>
          </label>

          <div className="rounded-lg border border-zinc-200 px-3 py-2 text-xs text-zinc-600">
            <div data-testid="ai-report-last-sent">
              {tr("Dernier envoi : ", "Last delivery: ")}
              {settings.last_sent_at
                ? new Date(settings.last_sent_at).toLocaleString(lang === "en" ? "en-CA" : "fr-CA", {
                    dateStyle: "long",
                    timeStyle: "short",
                    timeZone: "America/Toronto",
                  })
                : tr("jamais", "never")}
            </div>
            {settings.updated_by && (
              <div className="text-zinc-400 mt-0.5">
                {tr("Réglages modifiés par", "Settings changed by")} {settings.updated_by}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <ReportPreviewModal
              endpoint="/api/org/ai-package/report-preview"
              testid="ai-report-preview"
            />
            <button
              onClick={sendNow}
              disabled={sending || !selectedCount}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
              data-testid="ai-report-send-now"
            >
              <PaperPlaneTilt size={16} />
              {sending ? tr("Envoi…", "Sending…") : tr("Envoyer maintenant (test)", "Send now (test)")}
            </button>
          </div>
        </div>
      </div>

      <ReportSendLog
        endpoint="/api/org/ai-package/report-log"
        testid="ai-report-log"
        showAmount
        reloadKey={logKey}
      />
    </section>
  );
};

export default AIReportSettingsCard;
